import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useAudit } from "../../../../contexts/auditContext";
import { AuditDocumentVerification } from "./AuditDocumentVerification";
import { Input } from "../../../../components/ui/input";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FilesIcon,
  Loader2Icon,
  Pencil,
  CheckIcon,
  XIcon,
  Trash2Icon,
} from "lucide-react";
import { ACCEPTED_FILE_EXTENSIONS } from "../../../../lib/fileTypes";
import { validateFilesWithToast } from "../../../../lib/fileUploadValidation";
import { Button } from "../../../../components/ui/button";
import { usePresignedUpload } from "../../../../hooks/usePresignedUpload";
import { UploadProgressOverlay } from "../../../../components/UploadProgressOverlay";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";
import { fetchFastApi } from "../../../../api/fastapi";
import { AuditSession } from "../../../../models/audit/auditSession";
import type { DrySessionSourceRow } from "../../../../models/audit/procedureSessionTableShell";
import { useHookCache } from "../../../../lib/hookCache";
import { isDerivedChildDocument } from "../../../../lib/documentChildTypes";

export const AuditSampleList = ({
  sourceRows,
  procedureSessionId,
  controlSessionId,
  auditSession,
}: {
  sourceRows: DrySessionSourceRow[];
  procedureSessionId: string;
  controlSessionId: string;
  // Threaded through purely to feed legacy children (`AuditSampleView`,
  // `DropZoneForSampleUpload`) and `AuditDocumentVerification`'s
  // `uploadDocumentsToAuditSession` slot. Out of scope to refactor those.
  auditSession: AuditSession;
}) => {
  const { updateCurrentDocument } = useAudit();
  const { invalidate } = useHookCache();
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceRowId = searchParams.get("sourceRowId");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentSourceRowId, setCurrentSourceRowId] = useState<string | null>(
    null,
  );
  const sampleRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sourceRowToDelete, setSourceRowToDelete] = useState<string | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const hoverClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [hoverOverlayPosition, setHoverOverlayPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const portalTarget = typeof document !== "undefined" ? document.body : null;
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const hasScrolledToSelectedRow = useRef(false);

  const orderedSourceRows = useMemo(() => {
    return [...sourceRows].sort(
      (a, b) => (a.sampleNumber || 0) - (b.sampleNumber || 0),
    );
  }, [sourceRows]);

  // The slim list's server-computed `documentCount` doesn't match the
  // legacy badge semantics (approved matchItems, deduped by source
  // document, excluding child docs). Reproduce the legacy rule from the
  // full session's `matchItems` so the badge matches what users
  // historically saw. Falls back to the slim count if the legacy entry is
  // missing (e.g., session not yet hydrated).
  const docCountByRowId = useMemo(() => {
    const map = new Map<string, number>();
    for (const fullRow of auditSession.sourceRows) {
      const seen = new Set<string>();
      for (const item of fullRow.matchItems) {
        if (item.state !== "approved") continue;
        const docId = item.sourceDocument.id;
        if (
          docId &&
          !isDerivedChildDocument(item.sourceDocument) &&
          !seen.has(docId)
        ) {
          seen.add(docId);
        }
      }
      map.set(fullRow.id, seen.size);
    }
    return map;
  }, [auditSession.sourceRows]);

  const filteredSourceRows = useMemo(() => {
    if (!searchTerm || !orderedSourceRows) return orderedSourceRows;

    const normalizedSearch = searchTerm.toLowerCase().trim();

    return orderedSourceRows.filter((row, index) => {
      // Check if search matches sample number (index + 1)
      const sampleNumber = index + 1;
      if (
        normalizedSearch === String(sampleNumber) ||
        `sample ${sampleNumber}`.includes(normalizedSearch)
      ) {
        return true;
      }

      // Search in all data fields
      for (const key in row.data) {
        const value = String(row.data[key] || "").toLowerCase();
        if (value.includes(normalizedSearch)) {
          return true;
        }
      }
      return false;
    });
  }, [orderedSourceRows, searchTerm]);

  const currentSourceRow = useMemo(() => {
    return orderedSourceRows?.find((row) => row.id === currentSourceRowId);
  }, [orderedSourceRows, currentSourceRowId]);

  // Set current source row ID from URL params or query params when component loads
  useEffect(() => {
    if (sourceRowId && orderedSourceRows) {
      // For sample page URLs with sourceRowId in path
      const sourceRowExists = orderedSourceRows.find(
        (row) => row.id === sourceRowId,
      );
      if (sourceRowExists && currentSourceRowId !== sourceRowId) {
        setCurrentSourceRowId(sourceRowId);
      }
    } else if (orderedSourceRows && !currentSourceRowId) {
      setCurrentSourceRowId(orderedSourceRows[0].id);
    }
  }, [sourceRowId, orderedSourceRows, currentSourceRowId]);

  // Handle selectedSourceRow search param - scroll to and highlight the row
  useEffect(() => {
    const selectedSourceRow = searchParams.get("selectedSourceRow");

    if (
      selectedSourceRow &&
      orderedSourceRows &&
      !hasScrolledToSelectedRow.current
    ) {
      const targetRow = orderedSourceRows.find(
        (row) => row.id === selectedSourceRow,
      );

      if (targetRow && sampleRefs.current[selectedSourceRow]) {
        // Set as current source row
        setCurrentSourceRowId(selectedSourceRow);

        // Scroll to the row
        setTimeout(() => {
          const rowElement = sampleRefs.current[selectedSourceRow];
          if (rowElement && scrollContainerRef.current) {
            rowElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);

        // Highlight the row
        setHighlightedRowId(selectedSourceRow);

        // Remove highlight after animation
        setTimeout(() => {
          setHighlightedRowId(null);
        }, 2000);

        hasScrolledToSelectedRow.current = true;
      }
    }
  }, [searchParams, orderedSourceRows]);

  const getUpToThreeAttributes = useCallback(
    (sourceRow: DrySessionSourceRow) => {
      const attributes = Object.keys(sourceRow.data);
      const selectedAttributes: unknown[] = [];
      for (const attribute of attributes) {
        if (selectedAttributes.length >= 3) {
          break;
        }
        if (sourceRow.data[attribute]) {
          selectedAttributes.push(sourceRow.data[attribute]);
        }
      }
      return selectedAttributes.join(" • ");
    },
    [],
  );

  const handleSearchSampleList = useCallback((search: string) => {
    setSearchTerm(search);
  }, []);

  const handleSampleClick = useCallback(
    (selectedSourceRowId: string) => {
      setCurrentSourceRowId(selectedSourceRowId);
      updateCurrentDocument(null);
      // Persist the open row to the URL so refresh/back-nav and deep-links
      // restore it. The page stacks N procedure sessions; each list only
      // adopts the URL value when the id belongs to its own rows, so the
      // sibling lists' selections are untouched.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("sourceRowId", selectedSourceRowId);
          return next;
        },
        { replace: true },
      );
    },
    [setCurrentSourceRowId, updateCurrentDocument, setSearchParams],
  );

  const handleDeleteClick = useCallback(
    (sourceRowId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setSourceRowToDelete(sourceRowId);
      setDeleteDialogOpen(true);
      setHoveredRowId(null);
      setHoverOverlayPosition(null);
    },
    [],
  );

  const clearHoverTimeout = useCallback(() => {
    if (hoverClearTimeoutRef.current) {
      clearTimeout(hoverClearTimeoutRef.current);
      hoverClearTimeoutRef.current = null;
    }
  }, []);

  const scheduleHoverClear = useCallback(() => {
    clearHoverTimeout();
    hoverClearTimeoutRef.current = setTimeout(() => {
      setHoveredRowId(null);
      setHoverOverlayPosition(null);
    }, 120);
  }, [clearHoverTimeout]);

  const updateHoverOverlayPosition = useCallback((rowId: string) => {
    const rowElement = sampleRefs.current[rowId];
    if (!rowElement) return;

    const rect = rowElement.getBoundingClientRect();
    const isAboveViewport = rect.bottom <= 0;
    const isBelowViewport = rect.top >= window.innerHeight;

    if (isAboveViewport || isBelowViewport) {
      setHoverOverlayPosition(null);
      return;
    }

    const offset = 16;
    const top = Math.max(rect.top - offset, 4);
    const maxLeft = window.innerWidth - offset - 4;
    const desiredLeft = rect.right - offset;
    const left = Math.min(desiredLeft, maxLeft);
    setHoverOverlayPosition({
      top,
      left,
    });
  }, []);

  const handleRowHoverStart = useCallback(
    (rowId: string) => {
      clearHoverTimeout();
      setHoveredRowId(rowId);
      updateHoverOverlayPosition(rowId);
    },
    [clearHoverTimeout, updateHoverOverlayPosition],
  );

  const handleOverlayMouseEnter = useCallback(() => {
    clearHoverTimeout();
  }, [clearHoverTimeout]);

  const handleRowHoverEnd = useCallback(() => {
    scheduleHoverClear();
  }, [scheduleHoverClear]);

  const handleOverlayMouseLeave = useCallback(() => {
    scheduleHoverClear();
  }, [scheduleHoverClear]);

  useEffect(() => {
    return () => {
      if (hoverClearTimeoutRef.current) {
        clearTimeout(hoverClearTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!hoveredRowId) {
      setHoverOverlayPosition(null);
      return;
    }

    updateHoverOverlayPosition(hoveredRowId);

    const handleScrollOrResize = () => {
      updateHoverOverlayPosition(hoveredRowId);
    };

    const scrollContainer = scrollContainerRef.current;
    scrollContainer?.addEventListener("scroll", handleScrollOrResize);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      scrollContainer?.removeEventListener("scroll", handleScrollOrResize);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [hoveredRowId, updateHoverOverlayPosition]);

  useEffect(() => {
    if (
      hoveredRowId &&
      !filteredSourceRows?.some((row) => row.id === hoveredRowId)
    ) {
      setHoveredRowId(null);
      setHoverOverlayPosition(null);
    }
  }, [filteredSourceRows, hoveredRowId]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!sourceRowToDelete) return;

    setIsDeleting(true);
    try {
      await fetchFastApi({
        method: "DELETE",
        path: `/matching/match-session/${procedureSessionId}/source-row/${sourceRowToDelete}`,
      });

      toast.success("Sample deleted successfully");

      // If we deleted the currently selected source row, select another one
      if (sourceRowToDelete === currentSourceRowId) {
        const remainingRows = orderedSourceRows?.filter(
          (row) => row.id !== sourceRowToDelete,
        );
        if (remainingRows && remainingRows.length > 0) {
          setCurrentSourceRowId(remainingRows[0].id);
        } else {
          setCurrentSourceRowId(null);
        }
      }

      // Refresh the procedure session slices for this audit session.
      await invalidate(["procedure-session", procedureSessionId]);
      if (controlSessionId) {
        await invalidate(["control-session", controlSessionId, "status"]);
      }
    } catch (error) {
      console.error("Failed to delete source row:", error);
      toast.error("Failed to delete sample");
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setSourceRowToDelete(null);
    }
  }, [
    sourceRowToDelete,
    procedureSessionId,
    controlSessionId,
    currentSourceRowId,
    orderedSourceRows,
    setCurrentSourceRowId,
    invalidate,
  ]);

  return (
    <div className="border border-stone-200 p-4">
      <div className="flex flex-row gap-6">
        {/* left scroll pane */}
        <div className="flex flex-col gap-2 shrink-0">
          <div className="p-[1px] sticky top-0 bg-white">
            <Input
              placeholder="Search sample list"
              onChange={(e) => handleSearchSampleList(e.target.value)}
            />
          </div>
          <div
            className="overflow-y-auto max-h-[560px]"
            ref={scrollContainerRef}
          >
            {filteredSourceRows?.map((row) => (
              <div
                key={row.id}
                ref={(el) => {
                  sampleRefs.current[row.id] = el;
                }}
                className={`relative flex flex-col gap-2 mt-2 p-2 border border-transparent hover:bg-stone-50 cursor-pointer group overflow-visible w-[200px] transition-all ${
                  currentSourceRow?.id === row.id
                    ? "bg-stone-50 border-stone-200!"
                    : "bg-white"
                } ${
                  highlightedRowId === row.id ? "animate-pulse-highlight" : ""
                }`}
                onClick={() => handleSampleClick(row.id)}
                onMouseEnter={() => handleRowHoverStart(row.id)}
                onMouseLeave={handleRowHoverEnd}
                onFocus={() => handleRowHoverStart(row.id)}
                onBlur={handleRowHoverEnd}
                role="button"
              >
                <div className="flex flex-row items-center justify-between">
                  <div className="text-sm w-24">Sample {row.sampleNumber}</div>
                  <div className="flex items-center gap-1 px-2 py-0.5 text-stone-700 rounded  text-xs border border-stone-200">
                    <span>
                      {docCountByRowId.get(row.id) ?? row.documentCount ?? 0}
                    </span>
                    <span>Docs</span>
                  </div>
                </div>
                <div className="flex flex-row items-center text-sm">
                  {getUpToThreeAttributes(row)}
                </div>
              </div>
            ))}
          </div>
        </div>
        {currentSourceRow && (
          <DropZoneForSampleUpload
            auditSession={auditSession}
            sourceRowId={currentSourceRow.id}
            sampleNumber={currentSourceRow.sampleNumber ?? 0}
            className="relative grow overflow-y-auto max-h-[600px] min-w-0"
          >
            <div className="flex flex-col gap-4 relative min-w-0">
              <div className="shrink-0">
                <AuditSampleView
                  auditSession={auditSession}
                  sourceRow={currentSourceRow}
                />
              </div>
              <div>
                <AuditDocumentVerification
                  procedureSessionId={procedureSessionId}
                  controlSessionId={controlSessionId}
                  auditSourceRowId={currentSourceRow.id}
                  auditSession={auditSession}
                />
              </div>
            </div>
          </DropZoneForSampleUpload>
        )}
      </div>

      {hoveredRowId && hoverOverlayPosition && portalTarget
        ? createPortal(
            <div
              onMouseEnter={handleOverlayMouseEnter}
              onMouseLeave={handleOverlayMouseLeave}
              onFocus={handleOverlayMouseEnter}
              onBlur={handleOverlayMouseLeave}
              style={{
                position: "fixed",
                top: `${hoverOverlayPosition.top}px`,
                left: `${hoverOverlayPosition.left}px`,
                zIndex: 1000,
              }}
            >
              <Button
                variant="ghost"
                size="icon"
                className="shadow-sm h-6 w-6 text-stone-500 border border-stone-200 bg-white hover:bg-red-50"
                onClick={(e) => {
                  if (!hoveredRowId) return;
                  handleDeleteClick(hoveredRowId, e);
                }}
              >
                <Trash2Icon className="h-3 w-3" />
              </Button>
            </div>,
            portalTarget,
          )
        : null}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sample</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this sample? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Narrow shape used by the metadata table — covers both the slim
// `DrySessionSourceRow` (`data: Record<string, unknown>`) and the legacy
// hydrated `SourceRow` (`data: Record<string, JSONPrimitive>`) so both
// callers (Support tab + Documents tab) typecheck without forcing the
// Documents tab into the slim list rewire.
type SampleViewSourceRow = {
  id: string;
  data: Record<string, unknown>;
};

export const AuditSampleView = ({
  auditSession,
  sourceRow,
}: {
  auditSession: AuditSession;
  sourceRow: SampleViewSourceRow;
}) => {
  return (
    <AuditSampleMetadataTable
      auditSession={auditSession}
      sourceRow={sourceRow}
    />
  );
};

const AuditSampleMetadataTable = ({
  auditSession,
  sourceRow,
}: {
  auditSession: AuditSession;
  sourceRow: SampleViewSourceRow;
}) => {
  const { invalidate } = useHookCache();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  const validKeys = useMemo(() => {
    const keys = Object.keys(sourceRow.data).filter(
      (key) => sourceRow.data[key],
    );
    const lookup = auditSession.procedure.attributeLookup;
    return keys.sort(
      (a, b) =>
        (lookup[a]?.attributeOrder ?? Number.MAX_SAFE_INTEGER) -
        (lookup[b]?.attributeOrder ?? Number.MAX_SAFE_INTEGER),
    );
  }, [sourceRow.data, auditSession.procedure.attributeLookup]);

  const handleEdit = useCallback((key: string, value: unknown) => {
    setEditingKey(key);
    setEditValue(value == null ? "" : String(value));
  }, []);

  const handleSave = useCallback(
    async (attributeId: string) => {
      if (!auditSession) return;

      const attributeName =
        auditSession.procedure.attributeLookup[attributeId]?.name;
      if (!attributeName) return;

      setIsSaving(true);
      try {
        await fetchFastApi({
          method: "POST",
          path: `/matching/session/${auditSession.id}/source-row/${sourceRow.id}/edit`,
          body: {
            attribute_name: attributeName,
            new_value: editValue,
          },
        });

        toast.success("Value updated successfully");

        // Refresh the procedure-session slice (and let status hook drive the
        // control-session-level state).
        if (auditSession.id) {
          await invalidate(["procedure-session", auditSession.id]);
        }
      } catch (error) {
        console.error("Failed to update value:", error);
        toast.error("Failed to update value");
      } finally {
        setIsSaving(false);
        setEditingKey(null);
        setEditValue("");
      }
    },
    [auditSession, sourceRow.id, editValue, invalidate],
  );

  const handleCancel = useCallback(() => {
    setEditingKey(null);
    setEditValue("");
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, key: string) => {
      if (e.key === "Enter") {
        handleSave(key);
      } else if (e.key === "Escape") {
        handleCancel();
      }
    },
    [handleSave, handleCancel],
  );

  return (
    <div className="border border-stone-200 overflow-x-auto">
      <table className="min-w-full table-auto">
        <thead>
          <tr className="border-b border-stone-200 bg-stone-50">
            {validKeys.map((key) => (
              <th
                key={key}
                className="p-3 text-left text-sm font-medium text-stone-500 min-w-[150px]"
              >
                {auditSession?.procedure.attributeLookup[key]?.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {validKeys.map((key) => (
              <td
                key={key}
                className="p-3 text-sm text-stone-800 group relative hover:bg-stone-50"
              >
                {editingKey === key ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, key)}
                      className="h-10 text-base min-w-[200px]"
                      autoFocus
                      disabled={isSaving}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleSave(key)}
                      disabled={isSaving}
                      className="h-6 w-6 p-0 shrink-0 hover:border"
                    >
                      {isSaving ? (
                        <Loader2Icon className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckIcon className="h-4 w-4 text-green-600" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleCancel}
                      disabled={isSaving}
                      className="h-6 w-6 p-0 shrink-0 hover:border"
                    >
                      <XIcon className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <span>
                      {sourceRow.data[key] == null
                        ? ""
                        : String(sourceRow.data[key])}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(key, sourceRow.data[key])}
                      className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Pencil className="h-3 w-3 text-stone-400" />
                    </Button>
                  </div>
                )}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const DropZoneForSampleUpload = ({
  auditSession,
  sourceRowId,
  sampleNumber,
  children,
  className = "",
}: {
  auditSession: AuditSession;
  sourceRowId: string;
  sampleNumber: number;
  children: React.ReactNode;
  className?: string;
}) => {
  const { uploadDocumentsToAuditSession } = useAudit();
  const {
    state: presignedUploadState,
    uploadFiles: uploadFilesPresigned,
    cancel: cancelPresignedUpload,
    reset: resetPresignedUpload,
  } = usePresignedUpload();
  const [isDragging, setIsDragging] = useState(false);
  const [, setDragCounter] = useState(0);
  const [uploading, setUploading] = useState(false);

  const acceptedFileTypes = [...ACCEPTED_FILE_EXTENSIONS, "zip"];

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => {
      const next = prev + 1;
      if (next === 1) setIsDragging(true);
      return next;
    });
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => {
      const next = prev - 1;
      if (next <= 0) {
        setIsDragging(false);
        return 0;
      }
      return next;
    });
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      const validFiles = await validateFilesWithToast(files);
      if (validFiles.length === 0) return;
      const invalidFiles = validFiles.filter(
        (file) =>
          !acceptedFileTypes.includes(
            file.name
              .split(".")
              .pop()
              ?.toLowerCase() as (typeof acceptedFileTypes)[number],
          ),
      );

      if (invalidFiles.length > 0) {
        toast.error(
          `Invalid file type. Accepted types: ${acceptedFileTypes.join(", ")}`,
        );
        return;
      }

      setUploading(true);
      try {
        // Try presigned upload first (for large files)
        const result = await uploadFilesPresigned(validFiles);

        if (result.usedPresigned) {
          // Presigned upload was used - finalize with upload IDs
          await uploadDocumentsToAuditSession(
            auditSession,
            validFiles,
            sourceRowId,
            result.uploadIds,
          );
          resetPresignedUpload();
        } else {
          // Small files - use existing multipart flow
          await uploadDocumentsToAuditSession(
            auditSession,
            validFiles,
            sourceRowId,
          );
        }
      } catch (error) {
        // Don't show error for cancelled uploads
        if (error instanceof DOMException && error.name === "AbortError") {
          resetPresignedUpload();
          return;
        }
        toast.error("Error uploading documents");
      } finally {
        setUploading(false);
      }
    }
  };

  return (
    <div
      className={`relative ${className}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {(isDragging || uploading) && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center border border-dashed border-stone-600 bg-white/90 backdrop-blur-[1px] pointer-events-none">
          <div className="text-2xl font-semibold mb-2 flex flex-col items-center gap-2">
            <FilesIcon className="w-6 h-6" />
            Drop files into Sample {sampleNumber}
          </div>
          <div className="text-sm text-stone-500">
            Accepted file types: {acceptedFileTypes.join(", ")}
          </div>
          {uploading && (
            <div className="flex flex-row items-center gap-2 mt-4">
              <Loader2Icon className="w-4 h-4 animate-spin" />
              Uploading...
            </div>
          )}
        </div>
      )}
      {children}
      <UploadProgressOverlay
        state={presignedUploadState}
        onCancel={cancelPresignedUpload}
      />
    </div>
  );
};
